// src/utils/mockDataInvoices.ts
// Supplier invoice import, price catalogue, and live pricing
// Part of the Procurement module

// ── Types ─────────────────────────────────────────────────────────

export type InvoiceStatus =
  | 'UNMATCHED'       // imported but not linked to a PO
  | 'PO_MATCHED'      // matched to an existing PO
  | 'QUERIED'         // discrepancy flagged — waiting for supplier response
  | 'APPROVED'        // finance approved — ready to pay
  | 'PAID'            // payment sent

export type ImportMethod = 'PDF_UPLOAD' | 'CSV_IMPORT' | 'EMAIL_PARSE' | 'MANUAL'

export type LineMatchStatus =
  | 'CATALOGUE_MATCH'   // item found in catalogue — price within tolerance
  | 'PRICE_CHANGED'     // item found but price differs from catalogue
  | 'NEW_ITEM'          // item not yet in catalogue — will be added
  | 'PO_MATCHED'        // line matched to a specific PO line

export type PriceDirection = 'UP' | 'DOWN' | 'STABLE'

export type AlertSeverity = 'INFO' | 'WARNING' | 'CRITICAL'

export type CatalogueCategory =
  | 'CABLE_POWER'
  | 'CABLE_COMMS'
  | 'CONDUIT_FITTINGS'
  | 'SIGNAL_EQUIPMENT'
  | 'CIVIL_MATERIALS'
  | 'STEEL'
  | 'PPE'
  | 'CONSUMABLES'
  | 'FUEL'

// ── Display metadata ───────────────────────────────────────────────

export const INVOICE_STATUS_META: Record<InvoiceStatus, { label: string; bg: string; text: string }> = {
  UNMATCHED:  { label: 'Unmatched',   bg: 'bg-amber-100',  text: 'text-amber-700'  },
  PO_MATCHED: { label: 'PO matched',  bg: 'bg-blue-100',   text: 'text-blue-700'   },
  QUERIED:    { label: 'Queried',     bg: 'bg-orange-100', text: 'text-orange-700' },
  APPROVED:   { label: 'Approved',    bg: 'bg-green-100',  text: 'text-green-700'  },
  PAID:       { label: 'Paid',        bg: 'bg-teal-100',   text: 'text-teal-700'   },
}

export const CATEGORY_META: Record<CatalogueCategory, { label: string; icon: string; bg: string; text: string }> = {
  CABLE_POWER:      { label: 'Power cable',       icon: '⚡', bg: 'bg-blue-100',   text: 'text-blue-800'   },
  CABLE_COMMS:      { label: 'Comms cable',        icon: '🔌', bg: 'bg-indigo-100', text: 'text-indigo-800' },
  CONDUIT_FITTINGS: { label: 'Conduit & fittings', icon: '⬜', bg: 'bg-teal-100',   text: 'text-teal-800'   },
  SIGNAL_EQUIPMENT: { label: 'Signal equipment',   icon: '🚦', bg: 'bg-purple-100', text: 'text-purple-800' },
  CIVIL_MATERIALS:  { label: 'Civil materials',    icon: '🏗', bg: 'bg-amber-100',  text: 'text-amber-800'  },
  STEEL:            { label: 'Steel & fixings',    icon: '🔩', bg: 'bg-gray-100',   text: 'text-gray-800'   },
  PPE:              { label: 'PPE',                icon: '🪖', bg: 'bg-yellow-100', text: 'text-yellow-800' },
  CONSUMABLES:      { label: 'Consumables',        icon: '🧰', bg: 'bg-green-100',  text: 'text-green-800'  },
  FUEL:             { label: 'Fuel',               icon: '⛽', bg: 'bg-red-100',    text: 'text-red-800'    },
}

// ── Price history entry ────────────────────────────────────────────

export interface PriceHistoryEntry {
  date: string
  priceExGST: number
  supplierId: string
  supplierName: string
  invoiceRef: string
  jobRef?: string
  note?: string
}

// ── Catalogue item ────────────────────────────────────────────────

export interface CatalogueItem {
  id: string
  sku: string                      // internal catalogue SKU
  description: string
  category: CatalogueCategory
  unit: string
  currentPriceExGST: number        // last-known catalogue price
  lastUpdatedAt: string            // when price was last updated
  lastUpdatedFromInvoice: string   // invoice ref that last updated the price
  lastUpdatedSupplierId: string
  lastUpdatedSupplierName: string
  priceHistory: PriceHistoryEntry[]
  // Trend
  previousPriceExGST: number       // price before last update
  priceDirection: PriceDirection
  priceChangePct: number           // % change from previous
  // Live price
  livePrice?: number               // from supplier feed (may differ from catalogue)
  livePriceSource?: string
  livePriceUpdatedAt?: string
  livePriceDivergePct?: number     // % diff between live and catalogue
  // Flags
  isVolatile: boolean              // high price movement commodity
  volatilityNote?: string
  linkedSupplierId?: string        // preferred/primary supplier
}

// ── Invoice line item ─────────────────────────────────────────────

export interface InvoiceLineItem {
  id: string
  lineNo: number
  supplierSku?: string
  description: string
  catalogueItemId?: string         // matched catalogue item
  quantity: number
  unit: string
  unitPriceExGST: number
  totalExGST: number
  matchStatus: LineMatchStatus
  cataloguePrice?: number          // catalogue price for comparison
  priceChangePct?: number          // vs catalogue (+ = more expensive)
  priceDirection?: PriceDirection
  poRef?: string                   // PO this line is matched to
  notes?: string
}

export interface PriceAlert {
  catalogueItemId: string
  description: string
  oldPrice: number
  newPrice: number
  changePct: number
  direction: PriceDirection
  severity: AlertSeverity
}

// ── Supplier invoice ──────────────────────────────────────────────

export interface SupplierInvoice {
  id: string
  invoiceNumber: string
  supplierId: string
  supplierName: string
  supplierABN: string
  invoiceDate: string
  receivedDate: string
  dueDate: string
  jobId?: string
  jobRef?: string                  // e.g. TP-2026-001
  poRef?: string                   // primary PO matched
  status: InvoiceStatus
  importMethod: ImportMethod
  importedFilename?: string
  lineItems: InvoiceLineItem[]
  subtotalExGST: number
  gst: number
  totalIncGST: number
  priceAlerts: PriceAlert[]
  queriedItems?: string            // description of what's queried
  approvedById?: string
  approvedByName?: string
  approvedAt?: string
  notes?: string
  createdById: string
  createdByName: string
  createdAt: string
}

// ── Price catalogue ────────────────────────────────────────────────

export const MOCK_CATALOGUE: CatalogueItem[] = [
  // POWER CABLES — volatile (copper price driven)
  {
    id: 'cat-001', sku: 'CBL-3CE-6MM', description: '3C+E 6mm² Cu/XLPE/PVC power cable (Olex / Prysmian)',
    category: 'CABLE_POWER', unit: 'm',
    currentPriceExGST: 16.80, lastUpdatedAt: '2026-06-15T10:00:00Z',
    lastUpdatedFromInvoice: 'INV-OLX-2026-0441',
    lastUpdatedSupplierId: 'sup-olex', lastUpdatedSupplierName: 'Prysmian Group (Olex)',
    previousPriceExGST: 14.90, priceDirection: 'UP', priceChangePct: 12.8,
    livePrice: 17.20, livePriceSource: 'Prysmian Distributor Portal', livePriceUpdatedAt: '2026-06-24T08:00:00Z', livePriceDivergePct: 2.4,
    isVolatile: true, volatilityNote: 'Price linked to LME copper. Volatile in Q1–Q2 2026.',
    linkedSupplierId: 'sup-olex',
    priceHistory: [
      { date: '2025-10-01', priceExGST: 12.40, supplierId: 'sup-olex', supplierName: 'Prysmian Group', invoiceRef: 'INV-OLX-2025-0312' },
      { date: '2025-12-15', priceExGST: 13.20, supplierId: 'sup-olex', supplierName: 'Prysmian Group', invoiceRef: 'INV-OLX-2025-0489' },
      { date: '2026-02-20', priceExGST: 14.90, supplierId: 'sup-olex', supplierName: 'Prysmian Group', invoiceRef: 'INV-OLX-2026-0219', note: 'Copper surcharge applied' },
      { date: '2026-04-10', priceExGST: 15.60, supplierId: 'sup-olex', supplierName: 'Prysmian Group', invoiceRef: 'INV-OLX-2026-0338' },
      { date: '2026-06-15', priceExGST: 16.80, supplierId: 'sup-olex', supplierName: 'Prysmian Group', invoiceRef: 'INV-OLX-2026-0441', note: 'Copper at 14-year high' },
    ],
  },
  {
    id: 'cat-002', sku: 'CBL-3CE-16MM', description: '3C+E 16mm² Cu/XLPE/PVC power cable (Olex / Prysmian)',
    category: 'CABLE_POWER', unit: 'm',
    currentPriceExGST: 38.50, lastUpdatedAt: '2026-06-15T10:00:00Z',
    lastUpdatedFromInvoice: 'INV-OLX-2026-0441',
    lastUpdatedSupplierId: 'sup-olex', lastUpdatedSupplierName: 'Prysmian Group (Olex)',
    previousPriceExGST: 34.20, priceDirection: 'UP', priceChangePct: 12.6,
    livePrice: 39.10, livePriceSource: 'Prysmian Distributor Portal', livePriceUpdatedAt: '2026-06-24T08:00:00Z', livePriceDivergePct: 1.6,
    isVolatile: true, volatilityNote: 'Same copper exposure as 6mm². Large conductor — higher sensitivity.',
    linkedSupplierId: 'sup-olex',
    priceHistory: [
      { date: '2025-10-01', priceExGST: 28.90, supplierId: 'sup-olex', supplierName: 'Prysmian Group', invoiceRef: 'INV-OLX-2025-0312' },
      { date: '2026-02-20', priceExGST: 34.20, supplierId: 'sup-olex', supplierName: 'Prysmian Group', invoiceRef: 'INV-OLX-2026-0219' },
      { date: '2026-06-15', priceExGST: 38.50, supplierId: 'sup-olex', supplierName: 'Prysmian Group', invoiceRef: 'INV-OLX-2026-0441' },
    ],
  },
  {
    id: 'cat-003', sku: 'CBL-FO-SM9', description: 'OS2 single-mode fibre optic cable 12-core (Nexans)',
    category: 'CABLE_COMMS', unit: 'm',
    currentPriceExGST: 4.20, lastUpdatedAt: '2026-04-10T10:00:00Z',
    lastUpdatedFromInvoice: 'INV-BWD-2026-0312',
    lastUpdatedSupplierId: 'sup-blackwoods', lastUpdatedSupplierName: 'Blackwoods Australia',
    previousPriceExGST: 4.00, priceDirection: 'UP', priceChangePct: 5.0,
    livePrice: 4.20, livePriceSource: 'Blackwoods B2B Portal', livePriceUpdatedAt: '2026-06-24T08:00:00Z', livePriceDivergePct: 0,
    isVolatile: false,
    priceHistory: [
      { date: '2025-08-01', priceExGST: 3.80, supplierId: 'sup-blackwoods', supplierName: 'Blackwoods', invoiceRef: 'INV-BWD-2025-0611' },
      { date: '2026-01-15', priceExGST: 4.00, supplierId: 'sup-blackwoods', supplierName: 'Blackwoods', invoiceRef: 'INV-BWD-2026-0088' },
      { date: '2026-04-10', priceExGST: 4.20, supplierId: 'sup-blackwoods', supplierName: 'Blackwoods', invoiceRef: 'INV-BWD-2026-0312' },
    ],
  },

  // CONDUIT & FITTINGS — stable
  {
    id: 'cat-004', sku: 'CNT-UPVC-100', description: 'UPVC 100mm conduit 6m length (Iplex / Vinidex)',
    category: 'CONDUIT_FITTINGS', unit: 'm',
    currentPriceExGST: 6.40, lastUpdatedAt: '2026-03-20T10:00:00Z',
    lastUpdatedFromInvoice: 'INV-SRD-2026-0201',
    lastUpdatedSupplierId: 'sup-saferoads', lastUpdatedSupplierName: 'Saferoads Queensland',
    previousPriceExGST: 6.20, priceDirection: 'UP', priceChangePct: 3.2,
    livePrice: 6.40, livePriceSource: 'Saferoads B2B', livePriceUpdatedAt: '2026-06-24T08:00:00Z', livePriceDivergePct: 0,
    isVolatile: false,
    priceHistory: [
      { date: '2025-07-01', priceExGST: 5.90, supplierId: 'sup-saferoads', supplierName: 'Saferoads QLD', invoiceRef: 'INV-SRD-2025-0412' },
      { date: '2026-01-10', priceExGST: 6.20, supplierId: 'sup-saferoads', supplierName: 'Saferoads QLD', invoiceRef: 'INV-SRD-2026-0051' },
      { date: '2026-03-20', priceExGST: 6.40, supplierId: 'sup-saferoads', supplierName: 'Saferoads QLD', invoiceRef: 'INV-SRD-2026-0201' },
    ],
  },
  {
    id: 'cat-005', sku: 'PIT-UPVC-300', description: 'UPVC cable pit 300×450 with trafficable lid (Humes)',
    category: 'CONDUIT_FITTINGS', unit: 'each',
    currentPriceExGST: 245.00, lastUpdatedAt: '2026-03-20T10:00:00Z',
    lastUpdatedFromInvoice: 'INV-SRD-2026-0201',
    lastUpdatedSupplierId: 'sup-saferoads', lastUpdatedSupplierName: 'Saferoads Queensland',
    previousPriceExGST: 235.00, priceDirection: 'UP', priceChangePct: 4.3,
    livePrice: 248.00, livePriceSource: 'Saferoads B2B', livePriceUpdatedAt: '2026-06-24T08:00:00Z', livePriceDivergePct: 1.2,
    isVolatile: false,
    priceHistory: [
      { date: '2025-06-01', priceExGST: 218.00, supplierId: 'sup-saferoads', supplierName: 'Saferoads QLD', invoiceRef: 'INV-SRD-2025-0301' },
      { date: '2026-01-10', priceExGST: 235.00, supplierId: 'sup-saferoads', supplierName: 'Saferoads QLD', invoiceRef: 'INV-SRD-2026-0051' },
      { date: '2026-03-20', priceExGST: 245.00, supplierId: 'sup-saferoads', supplierName: 'Saferoads QLD', invoiceRef: 'INV-SRD-2026-0201' },
    ],
  },

  // SIGNAL EQUIPMENT — stable
  {
    id: 'cat-006', sku: 'SIG-LED-3ASP', description: 'LED traffic signal lantern 3-aspect (TMR TAER approved)',
    category: 'SIGNAL_EQUIPMENT', unit: 'each',
    currentPriceExGST: 680.00, lastUpdatedAt: '2026-05-01T10:00:00Z',
    lastUpdatedFromInvoice: 'INV-ALT-2026-0388',
    lastUpdatedSupplierId: 'sup-aldridge', lastUpdatedSupplierName: 'Aldridge Traffic Architects',
    previousPriceExGST: 680.00, priceDirection: 'STABLE', priceChangePct: 0,
    livePrice: 680.00, livePriceSource: 'Aldridge Portal', livePriceUpdatedAt: '2026-06-24T08:00:00Z', livePriceDivergePct: 0,
    isVolatile: false,
    priceHistory: [
      { date: '2025-01-01', priceExGST: 660.00, supplierId: 'sup-aldridge', supplierName: 'Aldridge', invoiceRef: 'INV-ALT-2025-0011' },
      { date: '2025-07-01', priceExGST: 680.00, supplierId: 'sup-aldridge', supplierName: 'Aldridge', invoiceRef: 'INV-ALT-2025-0388' },
      { date: '2026-05-01', priceExGST: 680.00, supplierId: 'sup-aldridge', supplierName: 'Aldridge', invoiceRef: 'INV-ALT-2026-0388' },
    ],
  },
  {
    id: 'cat-007', sku: 'SIG-CTRL-R900S', description: 'R900S SCATS controller cabinet (Aldridge / Surtec)',
    category: 'SIGNAL_EQUIPMENT', unit: 'each',
    currentPriceExGST: 14_800.00, lastUpdatedAt: '2026-05-01T10:00:00Z',
    lastUpdatedFromInvoice: 'INV-ALT-2026-0388',
    lastUpdatedSupplierId: 'sup-aldridge', lastUpdatedSupplierName: 'Aldridge Traffic Architects',
    previousPriceExGST: 14_200.00, priceDirection: 'UP', priceChangePct: 4.2,
    livePrice: 14_800.00, livePriceSource: 'Aldridge Portal', livePriceUpdatedAt: '2026-06-24T08:00:00Z', livePriceDivergePct: 0,
    isVolatile: false,
    priceHistory: [
      { date: '2024-06-01', priceExGST: 13_400.00, supplierId: 'sup-aldridge', supplierName: 'Aldridge', invoiceRef: 'INV-ALT-2024-0211' },
      { date: '2025-01-01', priceExGST: 14_200.00, supplierId: 'sup-aldridge', supplierName: 'Aldridge', invoiceRef: 'INV-ALT-2025-0011' },
      { date: '2026-05-01', priceExGST: 14_800.00, supplierId: 'sup-aldridge', supplierName: 'Aldridge', invoiceRef: 'INV-ALT-2026-0388' },
    ],
  },

  // CIVIL MATERIALS — moderate volatility (aggregate, fuel, labour driven)
  {
    id: 'cat-008', sku: 'CONC-G40-20MM', description: 'Ready-mix concrete Grade 40 20mm aggregate (Boral / Hanson)',
    category: 'CIVIL_MATERIALS', unit: 'm³',
    currentPriceExGST: 248.00, lastUpdatedAt: '2026-06-18T10:00:00Z',
    lastUpdatedFromInvoice: 'INV-BRL-2026-0441',
    lastUpdatedSupplierId: 'sup-boral', lastUpdatedSupplierName: 'Boral Construction Materials',
    previousPriceExGST: 228.00, priceDirection: 'UP', priceChangePct: 8.8,
    livePrice: 251.00, livePriceSource: 'Boral Online Portal', livePriceUpdatedAt: '2026-06-24T08:00:00Z', livePriceDivergePct: 1.2,
    isVolatile: true, volatilityNote: 'Influenced by diesel fuel surcharges and aggregate supply. Check current surcharge before pricing.',
    linkedSupplierId: 'sup-boral',
    priceHistory: [
      { date: '2025-06-01', priceExGST: 195.00, supplierId: 'sup-boral', supplierName: 'Boral', invoiceRef: 'INV-BRL-2025-0312' },
      { date: '2025-10-01', priceExGST: 210.00, supplierId: 'sup-boral', supplierName: 'Boral', invoiceRef: 'INV-BRL-2025-0488', note: 'Fuel surcharge +$8/m³' },
      { date: '2026-02-01', priceExGST: 228.00, supplierId: 'sup-boral', supplierName: 'Boral', invoiceRef: 'INV-BRL-2026-0198', note: 'Aggregate price increase' },
      { date: '2026-06-18', priceExGST: 248.00, supplierId: 'sup-boral', supplierName: 'Boral', invoiceRef: 'INV-BRL-2026-0441', note: 'Fuel surcharge now $22/m³' },
    ],
  },
  {
    id: 'cat-009', sku: 'CONC-G32-20MM', description: 'Ready-mix concrete Grade 32 20mm aggregate (Boral / Hanson)',
    category: 'CIVIL_MATERIALS', unit: 'm³',
    currentPriceExGST: 218.00, lastUpdatedAt: '2026-06-18T10:00:00Z',
    lastUpdatedFromInvoice: 'INV-BRL-2026-0441',
    lastUpdatedSupplierId: 'sup-boral', lastUpdatedSupplierName: 'Boral Construction Materials',
    previousPriceExGST: 202.00, priceDirection: 'UP', priceChangePct: 7.9,
    livePrice: 220.00, livePriceSource: 'Boral Online Portal', livePriceUpdatedAt: '2026-06-24T08:00:00Z', livePriceDivergePct: 0.9,
    isVolatile: true, volatilityNote: 'Fuel surcharge-linked. Track Boral portal before tendering.',
    priceHistory: [
      { date: '2025-06-01', priceExGST: 172.00, supplierId: 'sup-boral', supplierName: 'Boral', invoiceRef: 'INV-BRL-2025-0312' },
      { date: '2026-02-01', priceExGST: 202.00, supplierId: 'sup-boral', supplierName: 'Boral', invoiceRef: 'INV-BRL-2026-0198' },
      { date: '2026-06-18', priceExGST: 218.00, supplierId: 'sup-boral', supplierName: 'Boral', invoiceRef: 'INV-BRL-2026-0441' },
    ],
  },

  // STEEL — volatile (LME steel driven)
  {
    id: 'cat-010', sku: 'STL-REBAR-Y16', description: 'Deformed bar Y16 reinforcing steel (InfraBuild / OneSteel)',
    category: 'STEEL', unit: 'tonne',
    currentPriceExGST: 1_280.00, lastUpdatedAt: '2026-06-01T10:00:00Z',
    lastUpdatedFromInvoice: 'INV-SRD-2026-0488',
    lastUpdatedSupplierId: 'sup-saferoads', lastUpdatedSupplierName: 'Saferoads Queensland',
    previousPriceExGST: 1_440.00, priceDirection: 'DOWN', priceChangePct: -11.1,
    livePrice: 1_260.00, livePriceSource: 'InfraBuild Market Price', livePriceUpdatedAt: '2026-06-24T08:00:00Z', livePriceDivergePct: -1.6,
    isVolatile: true, volatilityNote: 'LME hot-rolled coil driven. Down 11% from Q4 2025 peak but still elevated vs 2024.',
    priceHistory: [
      { date: '2025-03-01', priceExGST: 1_080.00, supplierId: 'sup-saferoads', supplierName: 'Saferoads QLD', invoiceRef: 'INV-SRD-2025-0181' },
      { date: '2025-09-01', priceExGST: 1_320.00, supplierId: 'sup-saferoads', supplierName: 'Saferoads QLD', invoiceRef: 'INV-SRD-2025-0601', note: 'Global supply disruption' },
      { date: '2025-12-01', priceExGST: 1_440.00, supplierId: 'sup-saferoads', supplierName: 'Saferoads QLD', invoiceRef: 'INV-SRD-2025-0788', note: 'Peak — post-election infrastructure spend' },
      { date: '2026-06-01', priceExGST: 1_280.00, supplierId: 'sup-saferoads', supplierName: 'Saferoads QLD', invoiceRef: 'INV-SRD-2026-0488', note: 'Chinese demand eases' },
    ],
  },
  {
    id: 'cat-011', sku: 'STL-DIN-BASE', description: 'DIN base plate for signal poles (galvanised, bolts included)',
    category: 'STEEL', unit: 'each',
    currentPriceExGST: 420.00, lastUpdatedAt: '2026-04-15T10:00:00Z',
    lastUpdatedFromInvoice: 'INV-SRD-2026-0312',
    lastUpdatedSupplierId: 'sup-saferoads', lastUpdatedSupplierName: 'Saferoads Queensland',
    previousPriceExGST: 395.00, priceDirection: 'UP', priceChangePct: 6.3,
    livePrice: 420.00, livePriceSource: 'Saferoads B2B', livePriceUpdatedAt: '2026-06-24T08:00:00Z', livePriceDivergePct: 0,
    isVolatile: false,
    priceHistory: [
      { date: '2025-01-01', priceExGST: 365.00, supplierId: 'sup-saferoads', supplierName: 'Saferoads QLD', invoiceRef: 'INV-SRD-2025-0012' },
      { date: '2025-08-01', priceExGST: 395.00, supplierId: 'sup-saferoads', supplierName: 'Saferoads QLD', invoiceRef: 'INV-SRD-2025-0512' },
      { date: '2026-04-15', priceExGST: 420.00, supplierId: 'sup-saferoads', supplierName: 'Saferoads QLD', invoiceRef: 'INV-SRD-2026-0312' },
    ],
  },

  // PPE
  {
    id: 'cat-012', sku: 'PPE-BOOT-STLCAP', description: "Blundstone 8\" steel cap boot (Class 1) — supply per pair",
    category: 'PPE', unit: 'pair',
    currentPriceExGST: 178.00, lastUpdatedAt: '2026-05-10T10:00:00Z',
    lastUpdatedFromInvoice: 'INV-BWD-2026-0419',
    lastUpdatedSupplierId: 'sup-blackwoods', lastUpdatedSupplierName: 'Blackwoods Australia',
    previousPriceExGST: 168.00, priceDirection: 'UP', priceChangePct: 6.0,
    livePrice: 178.00, livePriceSource: 'Blackwoods B2B Portal', livePriceUpdatedAt: '2026-06-24T08:00:00Z', livePriceDivergePct: 0,
    isVolatile: false,
    priceHistory: [
      { date: '2025-01-01', priceExGST: 155.00, supplierId: 'sup-blackwoods', supplierName: 'Blackwoods', invoiceRef: 'INV-BWD-2025-0042' },
      { date: '2025-07-01', priceExGST: 168.00, supplierId: 'sup-blackwoods', supplierName: 'Blackwoods', invoiceRef: 'INV-BWD-2025-0388' },
      { date: '2026-05-10', priceExGST: 178.00, supplierId: 'sup-blackwoods', supplierName: 'Blackwoods', invoiceRef: 'INV-BWD-2026-0419' },
    ],
  },
  {
    id: 'cat-013', sku: 'PPE-HIVIZ-D', description: 'Hi-vis vest Class D (HVSQ compliant)',
    category: 'PPE', unit: 'each',
    currentPriceExGST: 38.00, lastUpdatedAt: '2026-05-10T10:00:00Z',
    lastUpdatedFromInvoice: 'INV-BWD-2026-0419',
    lastUpdatedSupplierId: 'sup-blackwoods', lastUpdatedSupplierName: 'Blackwoods Australia',
    previousPriceExGST: 36.00, priceDirection: 'UP', priceChangePct: 5.6,
    livePrice: 38.00, livePriceSource: 'Blackwoods B2B Portal', livePriceUpdatedAt: '2026-06-24T08:00:00Z', livePriceDivergePct: 0,
    isVolatile: false,
    priceHistory: [
      { date: '2025-01-01', priceExGST: 32.00, supplierId: 'sup-blackwoods', supplierName: 'Blackwoods', invoiceRef: 'INV-BWD-2025-0042' },
      { date: '2026-05-10', priceExGST: 38.00, supplierId: 'sup-blackwoods', supplierName: 'Blackwoods', invoiceRef: 'INV-BWD-2026-0419' },
    ],
  },

  // FUEL — highly volatile
  {
    id: 'cat-014', sku: 'FUEL-DIESEL-BULK', description: 'Diesel fuel — bulk delivery (site/generator use)',
    category: 'FUEL', unit: 'L',
    currentPriceExGST: 1.98, lastUpdatedAt: '2026-06-10T10:00:00Z',
    lastUpdatedFromInvoice: 'INV-FUL-2026-0412',
    lastUpdatedSupplierId: 'sup-puma', lastUpdatedSupplierName: 'Puma Energy (QLD)',
    previousPriceExGST: 2.18, priceDirection: 'DOWN', priceChangePct: -9.2,
    livePrice: 1.95, livePriceSource: 'Puma Energy Bulk Feed', livePriceUpdatedAt: '2026-06-24T08:00:00Z', livePriceDivergePct: -1.5,
    isVolatile: true, volatilityNote: 'AUD/USD and crude oil driven. Update before any tender with >500L fuel content.',
    priceHistory: [
      { date: '2025-06-01', priceExGST: 1.72, supplierId: 'sup-puma', supplierName: 'Puma Energy', invoiceRef: 'INV-FUL-2025-0288' },
      { date: '2025-10-01', priceExGST: 2.05, supplierId: 'sup-puma', supplierName: 'Puma Energy', invoiceRef: 'INV-FUL-2025-0512', note: 'Crude spike +18%' },
      { date: '2026-01-15', priceExGST: 2.18, supplierId: 'sup-puma', supplierName: 'Puma Energy', invoiceRef: 'INV-FUL-2026-0088', note: 'Peak Q1 2026' },
      { date: '2026-06-10', priceExGST: 1.98, supplierId: 'sup-puma', supplierName: 'Puma Energy', invoiceRef: 'INV-FUL-2026-0412', note: 'OPEC+ output increase' },
    ],
  },
]

// ── Mock invoices ──────────────────────────────────────────────────

export const MOCK_INVOICES: SupplierInvoice[] = [

  // Invoice 1: Boral — concrete supply — APPROVED — matched to PO
  {
    id: 'inv-001', invoiceNumber: 'BCQ-INV-2026-0441',
    supplierId: 'sup-boral', supplierName: 'Boral Construction Materials Group Ltd',
    supplierABN: '12 004 796 249',
    invoiceDate: '2026-06-18', receivedDate: '2026-06-19', dueDate: '2026-07-18',
    jobId: 'j1', jobRef: 'TP-2026-001', poRef: 'PO-TP001-001',
    status: 'APPROVED',
    importMethod: 'PDF_UPLOAD', importedFilename: 'BCQ-INV-2026-0441.pdf',
    lineItems: [
      { id: 'li-001-1', lineNo: 1, supplierSku: 'BRL-CONC-G40-20', description: 'Ready-mix concrete Grade 40 20mm — delivery BCQ-2026-441', catalogueItemId: 'cat-008', quantity: 0.5, unit: 'm³', unitPriceExGST: 248.00, totalExGST: 124.00, matchStatus: 'CATALOGUE_MATCH', cataloguePrice: 248.00, priceChangePct: 0, priceDirection: 'STABLE' },
    ],
    subtotalExGST: 124.00, gst: 12.40, totalIncGST: 136.40,
    priceAlerts: [],
    approvedById: 'u4', approvedByName: 'Sarah Lee', approvedAt: '2026-06-19T11:00:00Z',
    createdById: 'u4', createdByName: 'Sarah Lee', createdAt: '2026-06-19T10:00:00Z',
    notes: 'Foundation pour TP-2026-001 NW corner. Batch BCQ-2026-441.',
  },

  // Invoice 2: Olex/Prysmian — cable supply — PO_MATCHED — price alert!
  {
    id: 'inv-002', invoiceNumber: 'INV-OLX-2026-0441',
    supplierId: 'sup-olex', supplierName: 'Prysmian Group Australia Pty Ltd (Olex)',
    supplierABN: '67 091 231 772',
    invoiceDate: '2026-06-15', receivedDate: '2026-06-16', dueDate: '2026-07-15',
    jobId: 'j1', jobRef: 'TP-2026-001', poRef: 'PO-TP001-004',
    status: 'PO_MATCHED',
    importMethod: 'PDF_UPLOAD', importedFilename: 'Olex-Invoice-OLX-2026-0441.pdf',
    lineItems: [
      {
        id: 'li-002-1', lineNo: 1, supplierSku: 'OLX-3CE-6MM',
        description: '3C+E 6mm² Cu/XLPE/PVC cable — Batch OX-24-168, OX-24-169',
        catalogueItemId: 'cat-001',
        quantity: 850, unit: 'm', unitPriceExGST: 16.80, totalExGST: 14_280.00,
        matchStatus: 'PRICE_CHANGED', cataloguePrice: 14.90,
        priceChangePct: 12.8, priceDirection: 'UP',
        notes: 'Price up from last invoice ($14.90/m). Copper surcharge applied.',
      },
    ],
    subtotalExGST: 14_280.00, gst: 1_428.00, totalIncGST: 15_708.00,
    priceAlerts: [
      {
        catalogueItemId: 'cat-001',
        description: '3C+E 6mm² power cable — copper surcharge applied',
        oldPrice: 14.90, newPrice: 16.80, changePct: 12.8,
        direction: 'UP', severity: 'WARNING',
      },
    ],
    createdById: 'u2', createdByName: 'Jane Smith', createdAt: '2026-06-16T09:00:00Z',
    notes: 'Cable for TP-2026-001 circuits C1–C4. Price up 12.8% vs catalogue — copper surcharge. Catalogue updated.',
  },

  // Invoice 3: Aldridge — signal heads — QUERIED — quantity discrepancy
  {
    id: 'inv-003', invoiceNumber: 'ALT-INV-2026-0388',
    supplierId: 'sup-aldridge', supplierName: 'Aldridge Traffic Architects Pty Ltd',
    supplierABN: '42 007 135 844',
    invoiceDate: '2026-06-05', receivedDate: '2026-06-06', dueDate: '2026-07-05',
    jobId: 'j1', jobRef: 'TP-2026-001', poRef: 'PO-TP001-006',
    status: 'QUERIED',
    importMethod: 'PDF_UPLOAD', importedFilename: 'Aldridge-INV-2026-0388.pdf',
    lineItems: [
      {
        id: 'li-003-1', lineNo: 1, supplierSku: 'ATA-SIG-LED3',
        description: 'LED signal head 3-aspect 300mm (TMR TAER approved)',
        catalogueItemId: 'cat-006',
        quantity: 26, unit: 'each', unitPriceExGST: 680.00, totalExGST: 17_680.00,
        matchStatus: 'PO_MATCHED', cataloguePrice: 680.00, priceChangePct: 0, priceDirection: 'STABLE',
        notes: 'QUANTITY DISCREPANCY: PO-TP001-006 qty = 24. Invoice qty = 26. 2 extra heads not authorised.',
      },
      {
        id: 'li-003-2', lineNo: 2, supplierSku: 'ATA-CTRL-R900S',
        description: 'R900S SCATS controller cabinet (Aldridge / Surtec)',
        catalogueItemId: 'cat-007',
        quantity: 1, unit: 'each', unitPriceExGST: 14_800.00, totalExGST: 14_800.00,
        matchStatus: 'PO_MATCHED', cataloguePrice: 14_800.00, priceChangePct: 0, priceDirection: 'STABLE',
      },
    ],
    subtotalExGST: 32_480.00, gst: 3_248.00, totalIncGST: 35_728.00,
    priceAlerts: [],
    queriedItems: 'Line 1: Invoice qty 26 signal heads vs PO-TP001-006 qty 24. 2 unauthorised heads invoiced. Aldridge contacted 7 Jun — awaiting credit note.',
    createdById: 'u2', createdByName: 'Jane Smith', createdAt: '2026-06-06T09:00:00Z',
    notes: 'Queried with Aldridge — awaiting credit note for 2 excess signal heads ($1,360 ex GST).',
  },

  // Invoice 4: Blackwoods — PPE — UNMATCHED
  {
    id: 'inv-004', invoiceNumber: 'BWD-INV-2026-0419',
    supplierId: 'sup-blackwoods', supplierName: 'Blackwoods Australia',
    supplierABN: '59 004 400 405',
    invoiceDate: '2026-05-10', receivedDate: '2026-06-20', dueDate: '2026-06-10',
    status: 'UNMATCHED',
    importMethod: 'PDF_UPLOAD', importedFilename: 'Blackwoods-Tax-Invoice-BWD-2026-0419.pdf',
    lineItems: [
      { id: 'li-004-1', lineNo: 1, supplierSku: 'BWD-BOOT-SCAP-10', description: "Steel cap boot Blundstone 8\" US10", catalogueItemId: 'cat-012', quantity: 2, unit: 'pair', unitPriceExGST: 178.00, totalExGST: 356.00, matchStatus: 'CATALOGUE_MATCH', cataloguePrice: 178.00, priceChangePct: 0, priceDirection: 'STABLE' },
      { id: 'li-004-2', lineNo: 2, supplierSku: 'BWD-HIVIZ-D-XL', description: 'Hi-vis vest Class D XL — orange', catalogueItemId: 'cat-013', quantity: 4, unit: 'each', unitPriceExGST: 38.00, totalExGST: 152.00, matchStatus: 'CATALOGUE_MATCH', cataloguePrice: 38.00, priceChangePct: 0, priceDirection: 'STABLE' },
      { id: 'li-004-3', lineNo: 3, supplierSku: 'BWD-GLOV-CUT5', description: 'Cut-resistant gloves Level 5 (pack 12)', quantity: 2, unit: 'pack', unitPriceExGST: 48.00, totalExGST: 96.00, matchStatus: 'NEW_ITEM', notes: 'Not in catalogue — will be added as new item on approval.' },
    ],
    subtotalExGST: 604.00, gst: 60.40, totalIncGST: 664.40,
    priceAlerts: [],
    createdById: 'u3', createdByName: 'Sam Nguyen', createdAt: '2026-06-20T14:00:00Z',
    notes: 'PPE restock — no PO raised. Needs to be linked to a job or charged to overheads. OVERDUE — due 10 Jun.',
  },

  // Invoice 5: Saferoads — civil materials — APPROVED
  {
    id: 'inv-005', invoiceNumber: 'SRD-INV-2026-0488',
    supplierId: 'sup-saferoads', supplierName: 'Saferoads Queensland Pty Ltd',
    supplierABN: '89 124 603 271',
    invoiceDate: '2026-06-01', receivedDate: '2026-06-02', dueDate: '2026-07-01',
    jobId: 'j3', jobRef: 'TP-2026-003', poRef: 'PO-TP003-002',
    status: 'APPROVED',
    importMethod: 'CSV_IMPORT', importedFilename: 'Saferoads-Invoice-Export-June2026.csv',
    lineItems: [
      { id: 'li-005-1', lineNo: 1, supplierSku: 'SRD-DIN-BASE-F2', description: 'DIN base plate (Type F-2) for signal poles', catalogueItemId: 'cat-011', quantity: 6, unit: 'each', unitPriceExGST: 420.00, totalExGST: 2_520.00, matchStatus: 'CATALOGUE_MATCH', cataloguePrice: 420.00, priceChangePct: 0, priceDirection: 'STABLE' },
      { id: 'li-005-2', lineNo: 2, supplierSku: 'SRD-REBAR-Y16', description: 'Y16 reinforcing steel — signal pole cages (200 kg)', catalogueItemId: 'cat-010', quantity: 0.2, unit: 'tonne', unitPriceExGST: 1_280.00, totalExGST: 256.00, matchStatus: 'PRICE_CHANGED', cataloguePrice: 1_440.00, priceChangePct: -11.1, priceDirection: 'DOWN', notes: 'Reo price down 11% from Q4 2025 — catalogue updated.' },
      { id: 'li-005-3', lineNo: 3, supplierSku: 'SRD-UPVC-100', description: 'UPVC 100mm conduit 6m (qty 90 lengths = 540m)', catalogueItemId: 'cat-004', quantity: 540, unit: 'm', unitPriceExGST: 6.40, totalExGST: 3_456.00, matchStatus: 'CATALOGUE_MATCH', cataloguePrice: 6.40, priceChangePct: 0, priceDirection: 'STABLE' },
    ],
    subtotalExGST: 6_232.00, gst: 623.20, totalIncGST: 6_855.20,
    priceAlerts: [
      { catalogueItemId: 'cat-010', description: 'Y16 reinforcing steel — price decreased (market correction)', oldPrice: 1_440.00, newPrice: 1_280.00, changePct: -11.1, direction: 'DOWN', severity: 'INFO' },
    ],
    approvedById: 'u4', approvedByName: 'Sarah Lee', approvedAt: '2026-06-03T10:00:00Z',
    createdById: 'u4', createdByName: 'Sarah Lee', createdAt: '2026-06-02T09:00:00Z',
    notes: 'TP-2026-003 Stage 1 civil materials. CSV imported from Saferoads B2B. Reo price drop noted — catalogue updated.',
  },
]

// ── Helpers ───────────────────────────────────────────────────────

export function getInvoicesForJob(jobId: string): SupplierInvoice[] {
  return MOCK_INVOICES.filter(inv => inv.jobId === jobId)
}

export function getTotalAlerts(): number {
  return MOCK_INVOICES.reduce((s, inv) => s + inv.priceAlerts.length, 0)
}

export function getVolatileItems(): CatalogueItem[] {
  return MOCK_CATALOGUE.filter(c => c.isVolatile)
}

export function getItemsWithLiveDivergence(thresholdPct = 1): CatalogueItem[] {
  return MOCK_CATALOGUE.filter(c => c.livePriceDivergePct && Math.abs(c.livePriceDivergePct) >= thresholdPct)
}

export function getItemsNeedingUpdate(days = 30): CatalogueItem[] {
  const cutoff = new Date(Date.now() - days * 86400000).toISOString()
  return MOCK_CATALOGUE.filter(c => c.lastUpdatedAt < cutoff)
}

export function formatPriceChange(pct: number): string {
  if (Math.abs(pct) < 0.1) return 'No change'
  const sign = pct > 0 ? '+' : ''
  return `${sign}${pct.toFixed(1)}%`
}
